import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeatureComparison = ({ currentPlan, subscriptionData }) => {
  const features = [
    {
      category: 'Profil et Visibilité',
      items: [
        {
          name: 'Profil de base',
          free: true,
          professional: true,
          enterprise: true,
          description: 'Création et gestion du profil artistique'
        },
        {
          name: 'Badge Premium',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Badge distinctif sur votre profil'
        },
        {
          name: 'Galerie multimédia',
          free: '5 médias',
          professional: 'Illimité',
          enterprise: 'Illimité',
          description: 'Photos, vidéos et audio de vos performances'
        },
        {
          name: 'Boost de profil',
          free: false,
          professional: '1x par mois',
          enterprise: '3x par mois',
          description: 'Apparaître en priorité dans les résultats'
        },
        {
          name: 'Position prioritaire',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Classement amélioré dans les recherches'
        }
      ]
    },
    {
      category: 'Communication',
      items: [
        {
          name: 'Messages privés',
          free: '10/mois',
          professional: 'Illimité',
          enterprise: 'Illimité',
          description: 'Communication directe avec autres utilisateurs'
        },
        {
          name: 'Notifications avancées',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Alertes personnalisées pour opportunités'
        },
        {
          name: 'Réponse automatique',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Messages automatiques personnalisables'
        }
      ]
    },
    {
      category: 'Recherche et Découverte',
      items: [
        {
          name: 'Recherche simple',
          free: true,
          professional: true,
          enterprise: true,
          description: 'Recherche de base par nom et localisation'
        },
        {
          name: 'Filtres avancés',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Recherche par genre, expérience, disponibilité'
        },
        {
          name: 'Recommandations IA',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Suggestions personnalisées de connexions'
        },
        {
          name: 'Alertes de matching',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Notifications automatiques de nouvelles opportunités'
        }
      ]
    },
    {
      category: 'Analytics et Insights',
      items: [
        {
          name: 'Statistiques de base',
          free: true,
          professional: true,
          enterprise: true,
          description: 'Vues de profil et statistiques simples'
        },
        {
          name: 'Analytics détaillées',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Rapports complets sur votre performance'
        },
        {
          name: 'Tracking des conversions',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Suivi des opportunités transformées en contrats'
        },
        {
          name: 'Rapports personnalisés',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Création de rapports sur-mesure'
        }
      ]
    },
    {
      category: 'Support et Services',
      items: [
        {
          name: 'Support communautaire',
          free: true,
          professional: true,
          enterprise: true,
          description: 'Accès au forum et FAQ'
        },
        {
          name: 'Support prioritaire',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Réponse sous 24h par email'
        },
        {
          name: 'Gestionnaire de compte',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Contact dédié pour vos besoins spécifiques'
        },
        {
          name: 'Formation personnalisée',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Onboarding et formation sur-mesure'
        }
      ]
    },
    {
      category: 'Événements et Réseau',
      items: [
        {
          name: 'Événements publics',
          free: true,
          professional: true,
          enterprise: true,
          description: 'Accès aux événements de networking ouverts'
        },
        {
          name: 'Événements premium',
          free: false,
          professional: true,
          enterprise: true,
          description: 'Événements exclusifs pour membres premium'
        },
        {
          name: 'Événements VIP',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Événements haute gamme avec l\'élite artistique'
        },
        {
          name: 'Invitations privées',
          free: false,
          professional: false,
          enterprise: true,
          description: 'Invitations à des événements fermés sur invitation'
        }
      ]
    }
  ];

  const renderFeatureValue = (value, planType) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon name="Check" size={18} className="text-success" />
      ) : (
        <Icon name="X" size={18} className="text-muted-foreground" />
      );
    }
    
    if (typeof value === 'string') {
      return (
        <span className={`text-sm ${value === 'Illimité' ? 'text-success font-medium' : 'text-card-foreground'}`}>
          {value}
        </span>
      );
    }
    
    return null;
  };

  const getPlanColor = (plan) => {
    const colors = {
      free: 'text-muted-foreground',
      professional: 'text-accent',
      enterprise: 'text-primary'
    };
    return colors?.[plan] || colors?.free;
  };

  const getPlanName = (plan) => {
    const names = {
      free: 'Artiste',
      professional: 'Professionnel',
      enterprise: 'Entreprise'
    };
    return names?.[plan] || names?.free;
  };

  const isCurrentPlan = (plan) => plan === currentPlan;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-foreground mb-4">
          Comparaison des fonctionnalités
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Découvrez toutes les fonctionnalités disponibles selon votre plan d'abonnement
        </p>
      </div>

      {/* Mobile View - Cards */}
      <div className="lg:hidden space-y-6">
        {features?.map((category) => (
          <div key={category?.category} className="bg-card rounded-lg border border-border p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-card-foreground mb-4">
              {category?.category}
            </h3>
            <div className="space-y-4">
              {category?.items?.map((feature) => (
                <div key={feature?.name} className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-card-foreground">
                        {feature?.name}
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        {feature?.description}
                      </p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-muted/50 rounded p-2">
                      <div className="text-xs font-medium text-muted-foreground mb-1">Artiste</div>
                      <div className="flex justify-center">
                        {renderFeatureValue(feature?.free, 'free')}
                      </div>
                    </div>
                    <div className={`rounded p-2 ${currentPlan === 'professional' ? 'bg-accent/10 ring-2 ring-accent/20' : 'bg-muted/50'}`}>
                      <div className="text-xs font-medium text-accent mb-1">Pro</div>
                      <div className="flex justify-center">
                        {renderFeatureValue(feature?.professional, 'professional')}
                      </div>
                    </div>
                    <div className={`rounded p-2 ${currentPlan === 'enterprise' ? 'bg-primary/10 ring-2 ring-primary/20' : 'bg-muted/50'}`}>
                      <div className="text-xs font-medium text-primary mb-1">Entreprise</div>
                      <div className="flex justify-center">
                        {renderFeatureValue(feature?.enterprise, 'enterprise')}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop View - Table */}
      <div className="hidden lg:block">
        <div className="bg-card rounded-lg border border-border shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 bg-muted/50 p-6 border-b border-border">
            <div className="col-span-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Fonctionnalité
              </h3>
            </div>
            <div className="col-span-2 text-center">
              <div className={`text-sm font-semibold uppercase tracking-wider ${
                isCurrentPlan('free') ? 'text-foreground' : 'text-muted-foreground'
              }`}>
                Artiste
                {isCurrentPlan('free') && (
                  <div className="text-xs bg-success/10 text-success rounded-full px-2 py-0.5 mt-1 inline-block">
                    Plan actuel
                  </div>
                )}
              </div>
            </div>
            <div className="col-span-2 text-center">
              <div className={`text-sm font-semibold uppercase tracking-wider ${
                isCurrentPlan('professional') ? 'text-accent' : getPlanColor('professional')
              }`}>
                Professionnel
                {isCurrentPlan('professional') && (
                  <div className="text-xs bg-success/10 text-success rounded-full px-2 py-0.5 mt-1 inline-block">
                    Plan actuel
                  </div>
                )}
              </div>
            </div>
            <div className="col-span-2 text-center">
              <div className={`text-sm font-semibold uppercase tracking-wider ${
                isCurrentPlan('enterprise') ? 'text-primary' : getPlanColor('enterprise')
              }`}>
                Entreprise
                {isCurrentPlan('enterprise') && (
                  <div className="text-xs bg-success/10 text-success rounded-full px-2 py-0.5 mt-1 inline-block">
                    Plan actuel
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Feature Categories */}
          {features?.map((category) => (
            <div key={category?.category}>
              {/* Category Header */}
              <div className="bg-muted/30 px-6 py-3 border-b border-border">
                <h4 className="text-sm font-semibold text-foreground">
                  {category?.category}
                </h4>
              </div>
              
              {/* Category Features */}
              {category?.items?.map((feature, index) => (
                <div 
                  key={feature?.name}
                  className={`grid grid-cols-12 gap-4 p-6 border-b border-border hover:bg-muted/30 transition-colors ${
                    index === category?.items?.length - 1 ? 'border-b-0' : ''
                  }`}
                >
                  <div className="col-span-6">
                    <div className="space-y-1">
                      <h5 className="text-sm font-medium text-card-foreground">
                        {feature?.name}
                      </h5>
                      <p className="text-xs text-muted-foreground">
                        {feature?.description}
                      </p>
                    </div>
                  </div>
                  <div className="col-span-2 flex justify-center items-center">
                    {renderFeatureValue(feature?.free, 'free')}
                  </div>
                  <div className={`col-span-2 flex justify-center items-center ${
                    isCurrentPlan('professional') ? 'bg-accent/5 -mx-3 px-3 py-2 rounded' : ''
                  }`}>
                    {renderFeatureValue(feature?.professional, 'professional')}
                  </div>
                  <div className={`col-span-2 flex justify-center items-center ${
                    isCurrentPlan('enterprise') ? 'bg-primary/5 -mx-3 px-3 py-2 rounded' : ''
                  }`}>
                    {renderFeatureValue(feature?.enterprise, 'enterprise')}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Upgrade CTA */}
      {currentPlan === 'free' && (
        <div className="bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20 rounded-lg p-6">
          <div className="text-center">
            <Icon name="Crown" size={32} className="text-accent mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Prêt à passer au niveau supérieur ?
            </h3>
            <p className="text-muted-foreground mb-6">
              Débloquez toutes les fonctionnalités premium et développez votre carrière artistique.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                variant="default"
                iconName="Crown"
                iconPosition="left"
                iconSize={16}
                onClick={() => console.log('Upgrade to professional')}
              >
                Essayer Professionnel
              </Button>
              <Button
                variant="outline"
                iconName="Building"
                iconPosition="left"
                iconSize={16}
                onClick={() => console.log('Upgrade to enterprise')}
              >
                Découvrir Entreprise
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      <div className="bg-muted/30 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">
          Questions fréquentes
        </h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Puis-je changer de plan à tout moment ?
            </h4>
            <p className="text-sm text-muted-foreground">
              Oui, vous pouvez upgrader ou downgrader votre plan à tout moment. 
              Les changements prennent effet immédiatement avec ajustement proportionnel.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Que se passe-t-il si j'annule mon abonnement ?
            </h4>
            <p className="text-sm text-muted-foreground">
              Votre accès premium reste actif jusqu'à la fin de votre période de facturation, 
              puis votre compte revient automatiquement au plan gratuit.
            </p>
          </div>
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-foreground">
              Les données sont-elles conservées lors d'un changement de plan ?
            </h4>
            <p className="text-sm text-muted-foreground">
              Toutes vos données sont conservées. Seul l'accès aux fonctionnalités premium change 
              selon votre nouveau plan.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureComparison;